import type { LDFlagSet } from "launchdarkly-react-client-sdk";
import type { DefaultRootState } from "react-redux";
import type { ThunkAction } from "redux-thunk";

import { getAppsInBrandGroups } from "helpers/brand";
import type { IBrandGroup } from "helpers/transforms/entities";
import { getNewInventoryLocation } from "interfaces/app";

import type { IToolbarSnack, LoadingStatus } from "types/context";
import type { IDateRange } from "types/dates";

export type ActionTypes =
  | IReceiveEntitiesAction
  | IReceiveUpdateLDClientFlags
  | ISetAPITokenStatusAction
  | ISetLoadingStatusAction
  | ISetMessageStatusAction
  | ISetSelfRoutedAction
  | ISetToolbarSnack
  | IUpdateDateSelectionAction
  | IUpdateFilterText;

type ThunkResult<R> = ThunkAction<R, DefaultRootState, undefined, ActionTypes>;

export enum TypeKeys {
  RECEIVE_ENTITIES = "CONTEXT/RECEIVE_ENTITIES",
  SET_APP_API_TOKEN_STATUS = "CONTEXT/SET_APP_API_TOKEN_STATUS",
  SET_APP_LOADING_STATUS = "CONTEXT/SET_APP_LOADING_STATUS",
  SET_APP_MESSAGE_STATUS = "CONTEXT/SET_APP_MESSAGE_STATUS",
  SET_STORE_KEY = "CONTEXT/SET_STORE_KEY",
  SET_SELF_ROUTED_URL = "CONTEXT/SET_SELF_ROUTED_URL",
  SET_TOOLBAR_SNACK = "CONTEXT/SET_TOOLBAR_SNACK",
  UPDATE_DATE_SELECTION = "CONTEXT/UPDATE_DATE_SELECTION",
  UPDATE_FILTER_TEXT = "CONTEXT/UPDATE_FILTER_TEXT",
  UPDATE_LD_CLIENT_FLAGS = "CONTEXT/RECEIVE_INITIALIZED_LD_CLIENT",
}

export interface IReceiveEntitiesAction {
  type: TypeKeys.RECEIVE_ENTITIES;
  entities: IBrandGroup[];
  userApps: string[];
}

export interface IUpdateDateSelectionAction extends IDateRange {
  type: TypeKeys.UPDATE_DATE_SELECTION;
  pastOrFuture: PastFuture;
}

interface IUpdateFilterText {
  type: TypeKeys.UPDATE_FILTER_TEXT;
  text: string;
}

export const updateFilterText = (text: string): IUpdateFilterText => ({
  type: TypeKeys.UPDATE_FILTER_TEXT,
  text,
});

export enum PastFuture {
  PAST,
  FUTURE,
}

export const updateDateSelection = (
  pastOrFuture: PastFuture,
  dateRange: IDateRange
): IUpdateDateSelectionAction => ({
  type: TypeKeys.UPDATE_DATE_SELECTION,
  ...dateRange,
  pastOrFuture,
});

export const receiveEntities = (
  entities: IBrandGroup[]
): IReceiveEntitiesAction => ({
  type: TypeKeys.RECEIVE_ENTITIES,
  entities,
  userApps: getAppsInBrandGroups(entities),
});

interface IReceiveUpdateLDClientFlags {
  type: TypeKeys.UPDATE_LD_CLIENT_FLAGS;
  featureFlags: LDFlagSet;
}

export const updateLDClientFlags = (
  flags: LDFlagSet
): IReceiveUpdateLDClientFlags => ({
  type: TypeKeys.UPDATE_LD_CLIENT_FLAGS,
  featureFlags: flags,
});

export const switchInventoryLocation = (id: string): ThunkResult<void> => {
  return (): void => {
    getNewInventoryLocation(id);
  };
};

export interface ISetLoadingStatusAction {
  type: TypeKeys.SET_APP_LOADING_STATUS;
  app: string;
  status: LoadingStatus;
}

export const setAppLoadingStatus = (
  app: string,
  status: LoadingStatus
): ISetLoadingStatusAction => ({
  type: TypeKeys.SET_APP_LOADING_STATUS,
  app,
  status,
});

export interface ISetMessageStatusAction {
  type: TypeKeys.SET_APP_MESSAGE_STATUS;
  app: string;
  status: LoadingStatus;
}

export const setAppMessageStatus = (
  app: string,
  status: LoadingStatus
): ISetMessageStatusAction => ({
  type: TypeKeys.SET_APP_MESSAGE_STATUS,
  app,
  status,
});

export interface ISetAPITokenStatusAction {
  type: TypeKeys.SET_APP_API_TOKEN_STATUS;
  app: string;
  status: LoadingStatus;
  token?: string;
}

export function setAppAPITokenStatus(
  app: string,
  status: LoadingStatus,
  token?: string
): ISetAPITokenStatusAction {
  return {
    type: TypeKeys.SET_APP_API_TOKEN_STATUS,
    app,
    status,
    token,
  };
}

export interface ISetSelfRoutedAction {
  type: TypeKeys.SET_SELF_ROUTED_URL;
  route: string;
}

export function setSelfRouted(route: string) {
  return {
    type: TypeKeys.SET_SELF_ROUTED_URL,
    route,
  };
}

export interface ISetToolbarSnack {
  type: TypeKeys.SET_TOOLBAR_SNACK;
  snack: IToolbarSnack;
}

export function setToolbarSnack(snack: IToolbarSnack): ISetToolbarSnack {
  return {
    type: TypeKeys.SET_TOOLBAR_SNACK,
    snack: {
      // set auto timeout value for success type snacks (5s)
      timeout: snack.type === "success" ? 5000 : 0,
      ...snack,
    },
  };
}
